import { Button } from "components/actions"
import { Card, ErrorText } from "components/displays"

import { ErrorMessage } from "@hookform/error-message"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

import moment from "moment"

import { DateFilter, LocationSection } from "components/widgets/DashboardFilter/components"
import { FormType, getCompanyName, validationSchema } from "components/widgets/DashboardFilter/utils"
import { Select } from "components"
import { useProductCategory } from "pages/Inventori/Register/utils"
import { useState } from "react"
import { SelectOptionType } from "types"

type FilterType = FormType & {
    category_id?: string
    status?: string
}

type Props = {
    onChangeFilter: (value: FilterType) => void
    thirtyDatesMaximum?: boolean
    dropdownTurnover: SelectOptionType[]
}

export function FilterInventoryTurnOver({
    onChangeFilter,
    thirtyDatesMaximum,
    dropdownTurnover
}: Props): JSX.Element {

    const [fill, setFill] = useState<{
        category_id: string
        status: string
    }>({
        category_id: "",
        status: ""
    })

    const methods = useForm<FormType>({
        defaultValues: {
            date_end: moment().endOf("month").format("YYYY-MM-DD"),
            date_from: moment().startOf("month").format("YYYY-MM-DD"),
            type: "Month",
            location_id: ""
        },
        resolver: yupResolver(validationSchema)
    })

    const { control, formState, handleSubmit, setValue } = methods

    const date_end = useWatch({ control, name: "date_end" })
    const date_from = useWatch({ control, name: "date_from" })
    const type = useWatch({ control, name: "type" })

    const onSubmit = (value: FormType) => {
        onChangeFilter({
            ...value,
            category_id: fill.category_id,
            status: fill.status
        })
    }

    const { dataCategory, isLoadingCategory } = useProductCategory();

    const categoryOptions = [
        { label: "All", value: "" },
        ...(dataCategory ?? [])
    ]

    const statusOptions = [
  { label: "All", value: "" },
  ...(dropdownTurnover ?? [])
]


    return (
        <Card>
            <Card.Body>
                <section className="flex flex-wrap items-center justify-between gap-2">
                    <section>
                        <span className="font-bold text-[20px]">{getCompanyName()}</span>

                        <div className="mt-3">
                            <span>
                                {moment(date_from).format("DD MMMM YYYY")}
                                {" - "}
                                {moment(date_end).format("DD MMMM YYYY")}
                            </span>
                        </div>
                    </section>

                    <section className="w-fit flex gap-5 items-center">
                        <section className="flex gap-5 items-end">

                            {/* DATE FILTER */}
                            <section>
                                <DateFilter
                                    monthOnly
                                    thirtyDatesMaximum={thirtyDatesMaximum}
                                    disabled={type !== "Date"}
                                    value={{
                                        end_date: date_end,
                                        start_date: date_from,
                                        type
                                    }}
                                    onChangeEndDate={(res) => setValue("date_end", res)}
                                    onChangeStartDate={(res) => setValue("date_from", res)}
                                    onChangeType={(res) => setValue("type", res)}
                                />

                                <ErrorMessage
                                    name="type"
                                    errors={formState.errors}
                                    render={({ message }) => <ErrorText text={message} />}
                                />
                                <ErrorMessage
                                    name="date_from"
                                    errors={formState.errors}
                                    render={({ message }) => <ErrorText text={message} />}
                                />
                                <ErrorMessage
                                    name="date_end"
                                    errors={formState.errors}
                                    render={({ message }) => <ErrorText text={message} />}
                                />
                            </section>

                            {/* LOCATION */}
                            <FormProvider {...methods}>
                                <LocationSection />
                            </FormProvider>

                            {/* CATEGORY */}
                            <Select
                                className="w-[200px]"
                                label="CATEGORY"
                                options={categoryOptions}
                                isLoading={isLoadingCategory}
                                value={categoryOptions.find(
                                    item => item.value === fill.category_id
                                )}
                                onChange={(res) => {
                                    setFill(prev => ({
                                        ...prev,
                                        category_id: res?.value ?? ""
                                    }))
                                }}
                            />


                            {/* STATUS */}
                            <Select
                                className="w-[200px]"
                                label="STATUS"
                                options={statusOptions}
                                value={statusOptions.find(
                                    item => item.value === fill.status
                                )}
                                onChange={(res) => {
                                    setFill(prev => ({
                                        ...prev,
                                        status: res?.value ?? ""
                                    }))
                                }}
                            />

                            {/* SUBMIT */}
                            <section>
                                <Button
                                    type="submit"
                                    color="primary"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Submit
                                </Button>
                            </section>

                        </section>
                    </section>
                </section>
            </Card.Body>
        </Card>
    )
}
